// src/utils/progressTracker.js
// Piano With Pan — Progress Tracker
// ─────────────────────────────────────────────────────────────────────────
// Phase 1: localStorage-only. Captures every completed play so the parent
// progress report can summarise what their child has learnt.
//
// Future Phase 2: same API, but recordSession() will also push to Supabase
// (table: `practice_sessions`) keyed by user_id. The localStorage cache
// stays as the fast path / offline buffer.
//
// Schema of a session record:
//   {
//     id: string,            // uuid-ish
//     ts: number,            // Date.now() at finish
//     date: "YYYY-MM-DD",    // local-date bucket (for daily aggregation)
//     source: "level" | "lyn" | "practice",
//     levelId: number | string,
//     levelName: string,     // e.g. "Twinkle Twinkle", "CDE", "Free Play"
//     totalNotes: number,
//     correctNotes: number,
//     wrongNotes: number,
//     durationMs: number,    // play duration
//     stars: number | null,  // 0–3 if applicable
//   }
// ─────────────────────────────────────────────────────────────────────────

const SESSIONS_KEY = "pwp_progress_sessions_v1";
const STREAK_KEY   = "pwp_progress_streak_v1";
const MAX_SESSIONS = 500; // ring-buffer cap so localStorage doesn't bloat

/* ── Date helpers (all LOCAL time, not UTC) ─────────────────────────── */

export function ymd(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysBetween(ymdA, ymdB) {
  // Returns whole-day difference, treating each YYYY-MM-DD as a local-midnight date.
  const a = new Date(ymdA + "T00:00:00");
  const b = new Date(ymdB + "T00:00:00");
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function safeGet(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch {
    return fallback;
  }
}

function safeSet(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function makeId() {
  try {
    if (window?.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {}
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/* ── Sessions ──────────────────────────────────────────────────────── */

export function getSessions() {
  const arr = safeGet(SESSIONS_KEY, []);
  return Array.isArray(arr) ? arr : [];
}

export function recordSession(session) {
  if (!session || typeof session !== "object") return null;

  const total   = Math.max(0, Number(session.totalNotes)   || 0);
  const correct = Math.max(0, Number(session.correctNotes) || 0);
  const wrong   = Math.max(0, Number(session.wrongNotes)   || 0);

  const rec = {
    id:           makeId(),
    ts:           Date.now(),
    date:         ymd(),
    source:       session.source || "level",
    levelId:      session.levelId ?? null,
    levelName:    String(session.levelName || "Untitled").slice(0, 80),
    totalNotes:   total,
    correctNotes: correct,
    wrongNotes:   wrong,
    durationMs:   Math.max(0, Number(session.durationMs) || 0),
    stars:        Number.isFinite(session.stars) ? Math.max(0, Math.min(3, session.stars)) : null,
  };

  const all = getSessions();
  all.push(rec);

  // Trim oldest first if over cap
  if (all.length > MAX_SESSIONS) all.splice(0, all.length - MAX_SESSIONS);

  safeSet(SESSIONS_KEY, all);
  bumpStreak();
  return rec;
}

/* ── Streak ────────────────────────────────────────────────────────── */
// We bump the streak whenever a session is recorded:
//   • same day as lastActiveDate → streak stays the same
//   • exactly 1 day later → streak += 1
//   • more than 1 day later → streak resets to 1

const DEFAULT_STREAK = {
  current:        0,
  longest:        0,
  lastActiveDate: null, // YYYY-MM-DD
  startedAt:      null, // ts of streak start
};

export function getStreak() {
  const s = safeGet(STREAK_KEY, DEFAULT_STREAK);
  return { ...DEFAULT_STREAK, ...s };
}

function bumpStreak() {
  const today = ymd();
  const s = getStreak();

  let next;
  if (!s.lastActiveDate) {
    next = { current: 1, longest: 1, lastActiveDate: today, startedAt: Date.now() };
  } else {
    const diff = daysBetween(s.lastActiveDate, today);
    if (diff === 0) {
      // Same day — no change to streak count, just keep lastActiveDate as today
      next = { ...s, lastActiveDate: today };
    } else if (diff === 1) {
      const c = (s.current || 0) + 1;
      next = {
        current:        c,
        longest:        Math.max(s.longest || 0, c),
        lastActiveDate: today,
        startedAt:      s.startedAt || Date.now(),
      };
    } else {
      // Gap — reset
      next = { current: 1, longest: Math.max(s.longest || 0, 1), lastActiveDate: today, startedAt: Date.now() };
    }
  }

  safeSet(STREAK_KEY, next);
  return next;
}

/* ── Read-only streak status for UI (does NOT mutate) ──────────────── */
// Tells the UI three things:
//   - count: the streak count to show
//   - playedToday: did the kid play today?
//   - status: "active" (played today), "at_risk" (yesterday, not today),
//             "broken" (>1 day gap), or "fresh" (never played)
export function getStreakStatus() {
  const s = getStreak();
  const today = ymd();

  if (!s.lastActiveDate) {
    return { count: 0, playedToday: false, status: "fresh", longest: s.longest || 0 };
  }

  const diff = daysBetween(s.lastActiveDate, today);

  if (diff === 0) {
    return { count: s.current || 1, playedToday: true, status: "active", longest: s.longest || 0 };
  }
  if (diff === 1) {
    // Played yesterday — streak is "at risk" but not yet broken
    return { count: s.current || 1, playedToday: false, status: "at_risk", longest: s.longest || 0 };
  }
  // Gap of 2+ days — broken
  return { count: 0, playedToday: false, status: "broken", longest: s.longest || 0 };
}

/* ── Stats / Summaries ─────────────────────────────────────────────── */

// Daily target in minutes — match the Duolingo-style "15 min adventure"
export const DAILY_GOAL_MINUTES = 15;

export function todayMinutes() {
  const today = ymd();
  const ms = getSessions()
    .filter((s) => s.date === today)
    .reduce((sum, s) => sum + (s.durationMs || 0), 0);
  return Math.round(ms / 60_000);
}

export function dailyGoalPct() {
  return Math.min(100, Math.round((todayMinutes() / DAILY_GOAL_MINUTES) * 100));
}

// Aggregate summary for the parent report
export function getSummary() {
  const sessions = getSessions();
  if (sessions.length === 0) {
    return {
      totalSessions:    0,
      totalMinutes:     0,
      totalNotes:       0,
      totalCorrect:     0,
      totalWrong:       0,
      avgAccuracy:      null,
      uniqueDays:       0,
      uniqueSongs:      0,
      firstPlayedAt:    null,
      lastPlayedAt:     null,
      starsTotal:       0,
      perSong:          [],
      lastSessions:     [],
      dailyMinutes:     [], // last 14 days
      lynLevelsDone:    [],
      levelsCompleted:  [],
    };
  }

  const totalCorrect = sessions.reduce((s, x) => s + x.correctNotes, 0);
  const totalWrong   = sessions.reduce((s, x) => s + x.wrongNotes, 0);
  const totalNotes   = sessions.reduce((s, x) => s + x.totalNotes, 0);
  const totalMs      = sessions.reduce((s, x) => s + (x.durationMs || 0), 0);
  const totalStars   = sessions.reduce((s, x) => s + (x.stars || 0), 0);

  const uniqueDays = new Set(sessions.map((s) => s.date)).size;
  const uniqueSongs = new Set(sessions.map((s) => `${s.source}:${s.levelId}`)).size;

  // Per-song aggregate (best stars, plays, last played, avg accuracy)
  const perSongMap = {};
  for (const s of sessions) {
    const key = `${s.source}:${s.levelId}`;
    if (!perSongMap[key]) {
      perSongMap[key] = {
        key,
        source:       s.source,
        levelId:      s.levelId,
        levelName:    s.levelName,
        plays:        0,
        bestStars:    0,
        lastPlayedTs: 0,
        totalCorrect: 0,
        totalNotes:   0,
        totalMs:      0,
      };
    }
    const e = perSongMap[key];
    e.plays         += 1;
    e.bestStars      = Math.max(e.bestStars, s.stars || 0);
    e.lastPlayedTs   = Math.max(e.lastPlayedTs, s.ts);
    e.totalCorrect  += s.correctNotes;
    e.totalNotes    += s.totalNotes;
    e.totalMs       += s.durationMs || 0;
    e.levelName      = s.levelName; // keep most recent name
  }
  const perSong = Object.values(perSongMap)
    .map((e) => ({
      ...e,
      accuracy: e.totalNotes > 0 ? Math.round((e.totalCorrect / e.totalNotes) * 100) : null,
      minutes:  Math.round(e.totalMs / 60_000),
    }))
    .sort((a, b) => b.lastPlayedTs - a.lastPlayedTs);

  // Last 14 days of minutes (oldest → newest for charting)
  const dailyMinutes = [];
  const dayMap = {};
  for (const s of sessions) {
    dayMap[s.date] = (dayMap[s.date] || 0) + (s.durationMs || 0);
  }
  const today = new Date();
  for (let i = 13; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = ymd(d);
    dailyMinutes.push({
      date:    key,
      label:   d.toLocaleDateString(undefined, { weekday: "short" }),
      minutes: Math.round((dayMap[key] || 0) / 60_000),
    });
  }

  const lastSessions = [...sessions]
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 8);

  const lynLevelsDone = [
    ...new Set(
      sessions
        .filter((s) => s.source === "lyn" && (s.stars || 0) > 0)
        .map((s) => s.levelId)
    ),
  ];

  const levelsCompleted = [
    ...new Set(
      sessions
        .filter((s) => s.source === "level" && s.correctNotes >= s.totalNotes && s.totalNotes > 0)
        .map((s) => s.levelId)
    ),
  ];

  return {
    totalSessions:   sessions.length,
    totalMinutes:    Math.round(totalMs / 60_000),
    totalNotes,
    totalCorrect,
    totalWrong,
    avgAccuracy:     totalNotes > 0 ? Math.round((totalCorrect / totalNotes) * 100) : null,
    uniqueDays,
    uniqueSongs,
    firstPlayedAt:   Math.min(...sessions.map((s) => s.ts)),
    lastPlayedAt:    Math.max(...sessions.map((s) => s.ts)),
    starsTotal:      totalStars,
    perSong,
    lastSessions,
    dailyMinutes,
    lynLevelsDone,
    levelsCompleted,
  };
}

// For testing / parental clear
export function clearProgressData() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(SESSIONS_KEY);
    window.localStorage.removeItem(STREAK_KEY);
  } catch {}
}